import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBackdropClose } from '../hooks/useBackdropClose';
import SimManagerHeader from '../components/sim-manager-header';
import SimTable from '../components/sim-table';
import SimEditPanel from '../components/sim-edit-panel';
import SimAddPanel from '../components/sim-add-panel';
import SimFilterPanel from '../components/sim-filter-panel';
import SimInfoPanel from '../components/sim-info-panel';
import { checkAndFixToken } from '../utils/browserTokenCheck';

/**
 * SimManager — страница Sim-менеджер
 * По макету Figma 26-04 wa Proxy Manager (node 383-69904)
 * Интеграция с API /api/sim-cards
 */

/** Deep link из ProxyTable: ?modem_number= (номер модема) и опционально ?modem_server_id= (KrakenServer.id). */
const buildSimManagerFiltersFromUrlParams = (params) => {
  const f = {};
  const payment = params.get('payment');
  if (payment) {
    f.payment_filter = payment;
  }
  const mn = (params.get('modem_number') || '').trim();
  if (mn) {
    f.modem_number = mn;
  }
  const msid = (params.get('modem_server_id') || '').trim();
  if (msid) {
    f.modem_server_ids = msid;
  }
  return f;
};

// API базовый URL:
// - production: только same-origin (CSP connect-src 'self')
// - development: можно переопределить через REACT_APP_API_URL
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? ''
    : (process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : '');

// Маппинг статусов для отображения
const SIM_STATUS_LABELS = {
  working: 'Работает',
  free: 'Свободен',
  reserve_msk: 'Резерв Мск',
  reserve_spb: 'Резерв СПб',
  check: 'Проверить',
  replace: 'Заменить',
  frozen: 'Заморожен',
};

/** Токен фильтра столбца SIM: модем недоступен ≥72ч (совпадает с backend `SIM_FILTER_MODEM_72H_TOKEN`) */
const SIM_FILTER_MODEM_72H_TOKEN = 'modem_unavailable_72h';
const SIM_FILTER_MODEM_72H_LABEL = 'Модем >72ч недоступен (!)';

/** Несколько номеров из одного поля: перенос строки, запятая, точка с запятой */
const parseSimPhoneNumbersInput = (raw) => {
  if (raw == null || String(raw).trim() === '') {
    return [];
  }
  return String(raw)
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
};

const MODEM_STATUS_LABELS = {
  available: 'Доступен',
  unavailable: 'Недоступен',
  disabled: 'Отключен',
  unknown: 'Неизвестно',
};

const resolveHeaderModemStatusFilter = (chipStatus) => {
  if (!chipStatus || chipStatus === 'all') {
    return null;
  }
  return chipStatus;
};

const REALTIME_MODEM_STATUS_LABELS = {
  working: 'Доступен',
  available: 'Доступен',
  not_working: 'Недоступен',
  unavailable: 'Недоступен',
  offline: 'Недоступен',
  disabled: 'Отключен',
  unknown: 'Неизвестно',
};

// Опции для инлайн-редактирования (select)
const SIM_STATUS_OPTIONS = [
  { value: 'working', label: 'Работает' },
  { value: 'free', label: 'Свободен' },
  { value: 'reserve_spb', label: 'Резерв СПб' },
  { value: 'reserve_msk', label: 'Резерв Мск' },
  { value: 'check', label: 'Проверить' },
  { value: 'replace', label: 'Заменить' },
  { value: 'frozen', label: 'Заморожен' },
];

const OPERATOR_OPTIONS = [
  { value: 'МТС', label: 'МТС' },
  { value: 'Мегафон', label: 'Мегафон' },
  { value: 'Теле2', label: 'Теле2' },
  { value: 'Билайн', label: 'Билайн' },
  { value: 'Yota', label: 'Yota' },
];

const normalizeOperatorValue = (value) => {
  if (value === null || value === undefined) return value;
  const normalized = String(value).trim();
  if (!normalized) return '';

  const lower = normalized.toLowerCase();
  if (lower === 'tele2' || lower === 'теле2' || lower === 't2') return 'Теле2';
  if (lower === 'megafon' || lower === 'мегафон') return 'Мегафон';
  if (lower === 'mts' || lower === 'мтс') return 'МТС';
  if (lower === 'beeline' || lower === 'билайн') return 'Билайн';
  if (lower === 'yota' || lower === 'йота') return 'Yota';

  return normalized;
};

// Функция для получения токена (как в axios: нормализация demo_token → demo-token)
const getAuthToken = () => {
  const fixed = checkAndFixToken();
  if (fixed) return fixed;
  return sessionStorage.getItem('token') || '';
};

// Функция для API запросов
const fetchApi = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const controller = new AbortController();
  const timeoutMs = 20000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error(`Превышено время ожидания ответа (${Math.round(timeoutMs / 1000)}с)`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Ошибка сервера' }));
    let detail = error.detail;
    if (Array.isArray(detail)) {
      detail = detail
        .map((item) => (item && typeof item === 'object' && item.msg ? item.msg : String(item)))
        .join('; ');
    } else if (detail != null && typeof detail === 'object') {
      detail = JSON.stringify(detail);
    }
    throw new Error(detail || `HTTP ${response.status}`);
  }

  return response.json();
};

// Форматирование даты для отображения (API: ISO или YYYY-MM-DD)
const formatDate = (dateStr) => {
  if (dateStr == null || dateStr === '') return '';
  const s = typeof dateStr === 'string' ? dateStr.trim() : String(dateStr);
  if (!s) return '';
  // Только дата без времени — парсим как локальную календарную дату (без сдвига UTC)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map((n) => parseInt(n, 10));
    const date = new Date(y, m - 1, d);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Сокращение названий городов для столбца ГЕО
const GEO_SHORT_LABELS = {
  'Санкт-Петербург': 'СПб',
  'санкт-петербург': 'СПб',
  'СПб': 'СПб',
  'Москва': 'МСК',
  'москва': 'МСК',
  'МСК': 'МСК',
  'Мск': 'МСК',
};

const shortenGeo = (geo) => {
  if (!geo) return '';
  return GEO_SHORT_LABELS[geo] || geo;
};

const getModemNumberFromIP = (ipAddress) => {
  if (!ipAddress || typeof ipAddress !== 'string') return null;
  const parts = ipAddress.trim().split('.');
  if (parts.length !== 4) return null;
  const octet = Number.parseInt(parts[2], 10);
  if (!Number.isFinite(octet)) return null;
  return octet;
};

const getConnectionModemNumber = (modem) => {
  if (!modem || typeof modem !== 'object') return null;
  // Учётный № только из modem_internal_ip; без fallback на ip_address / kraken_id.
  return getModemNumberFromIP(modem.modem_internal_ip);
};

// Преобразование данных API в формат таблицы
const transformSimData = (item) => {
  const modemStatusValue = item.modem_status || 'unknown';
  const modemStatusLabel = REALTIME_MODEM_STATUS_LABELS[modemStatusValue]
    || MODEM_STATUS_LABELS[modemStatusValue]
    || modemStatusValue
    || 'Неизвестно';
  const modemNumberText = item.modem_number != null ? `№ ${item.modem_number}` : '';
  const modemNameText = String(item.modem_name || '').trim();
  const modemDisplayText = modemNumberText
    ? `${modemNumberText}${modemNameText ? ` ${modemNameText}` : ''}`
    : modemNameText;

  return ({
  id: item.id,
  modemStatus: modemStatusLabel,
  simStatus: SIM_STATUS_LABELS[item.sim_status] || item.sim_status || 'Свободен',
  geo: shortenGeo(item.geo),
  server: item.server_name || '',
  serverDisabled: Boolean(item.server_disabled),
  modemNumber: modemDisplayText,
  phoneNumber: item.phone_number || '',
  operator: item.operator || '',
  amount: item.amount || 0,
  lastPayment: formatDate(item.last_payment_date),
  nextPayment: formatDate(item.next_payment_date),
  freezeUntil: formatDate(item.freeze_until),
  registeredBy: item.personal_account?.name || '',
  registeredOn: item.registered_by || '',
  comment: item.comment || '',
  imei: item.imei || '',
  mac: item.mac_address || '',
  modemImei: item.modem_imei || '',
  modemMac: item.modem_mac_address || '',
  accountLink: item.account_link || '',
  personalAccount: item.personal_account || null,
  personalAccountId: item.personal_account_id || null,
  modemLongUnavailable72h: Boolean(item.modem_long_unavailable_72h),
  _raw: item,
  });
};

const normalizeModemStatusKey = (statusValue) => {
  const status = String(statusValue || '').toLowerCase().trim();

  if (!status) return null;
  if (
    status === 'disabled'
    || status.includes('отключ')
  ) {
    return 'disabled';
  }
  if (status.includes('недоступен') || status === 'unavailable' || status === 'not_working' || status === 'offline') {
    return 'unavailable';
  }
  if (status.includes('неизвест') || status === 'unknown') {
    return 'unknown';
  }
  if (status.includes('доступен') || status === 'available' || status === 'working') {
    return 'available';
  }

  return null;
};

const SimManager = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeStatus, setActiveStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [paymentConfirmRow, setPaymentConfirmRow] = useState(null);
  // Затемнение закрывает подтверждение оплаты только настоящим щелчком (владелец 29.10, DF85)
  const paymentConfirmBackdrop = useBackdropClose(() => setPaymentConfirmRow(null));
  const [proxyCheckLoading, setProxyCheckLoading] = useState(false);

  // Статистика из API
  const [stats, setStats] = useState({
    total: 0,
    by_modem_status: { available: 0, unavailable: 0, unknown: 0, disabled: 0 },
    global_modem_status: {
      total: 0,
      available: 0,
      unavailable: 0,
      unknown: 0,
      unavailable_including_unknown: 0,
    },
    reserves: { msk: 0, spb: 0 },
    total_payments: 0,
    avg_tariff_price: 0,
    upcoming_payments: { today: 0, tomorrow: 0, '3days': 0, week: 0 },
  });

  // Данные таблицы из API
  const [simData, setSimData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Состояние выбранных строк
  const [selectedRows, setSelectedRows] = useState([]);

  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Сортировка
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });

  // Фильтры: ?payment= / ?modem_number= / ?modem_server_id=
  const [filters, setFilters] = useState(() =>
    buildSimManagerFiltersFromUrlParams(
      new URLSearchParams(typeof window !== 'undefined' ? window.location.search : ''),
    ),
  );

  const simManagerUrlSyncSkipFirstRef = useRef(false);
  useEffect(() => {
    if (!simManagerUrlSyncSkipFirstRef.current) {
      simManagerUrlSyncSkipFirstRef.current = true;
      return;
    }
    const mn = (searchParams.get('modem_number') || '').trim();
    const msid = (searchParams.get('modem_server_id') || '').trim();
    setFilters((prev) => {
      const next = { ...prev };
      if (mn) {
        next.modem_number = mn;
      } else {
        delete next.modem_number;
      }
      if (msid) {
        next.modem_server_ids = msid;
      } else {
        delete next.modem_server_ids;
      }
      return next;
    });
    setCurrentPage(1);
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.has('payment')) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Состояние панели редактирования
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);

  // Состояние панели добавления
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);

  // Данные подключений с модемами (для выбора модема в инлайн-редактировании)
  const [connections, setConnections] = useState([]);

  // Состояние панели фильтров
  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);

  // Состояние инфо-панели SIM
  const [infoRowId, setInfoRowId] = useState(null);
  const [isInfoPanelVisible, setIsInfoPanelVisible] = useState(false);
  /** Последняя строка, по которой кликнули в таблице (подсветка, не чекбоксы) */
  const [activeRowId, setActiveRowId] = useState(null);
  const [tableFiltersResetToken, setTableFiltersResetToken] = useState(0);
  const [scopedPanelStats, setScopedPanelStats] = useState(null);
  const scopedPanelRequestIdRef = useRef(0);

  // Опции для фильтров
  const [filterOptions, setFilterOptions] = useState({
    sim_statuses: [],
    sim_special_filters: [],
    operators: [],
    geos: [],
    servers: [],
    modem_numbers: [],
    phone_numbers: [],
    registered_by: [],
    personal_account_owners: [],
  });

  /** Шаблоны операторов (ссылки ЛК из Настройки → Операторы) */
  const [operatorTemplatesForLk, setOperatorTemplatesForLk] = useState([]);

  // Для UI фильтра "№ Модема" нужны уникальные value (иначе одинаковые номера отмечаются сразу все).
  // В API при этом продолжаем отправлять только исходный modem number.
  const modemNumberFilterOptions = useMemo(
    () => (filterOptions.modem_numbers || []).map((m, index) => ({
      value: `${m.value}__${index}`,
      label: m.label,
      rawValue: String(m.value),
    })),
    [filterOptions.modem_numbers]
  );

  const modemNumberFilterOptionsByServer = useMemo(
    () => connections.reduce((acc, conn) => {
      const key = conn.name;
      const options = (conn.modems || []).map((modem) => ({
        value: `${key}__${modem.id}`,
        label: `№ ${getConnectionModemNumber(modem)} ${modem.name || ''}`.trim(),
        rawValue: String(getConnectionModemNumber(modem)),
      }));
      acc[key] = options;
      return acc;
    }, {}),
    [connections]
  );

  const modemNumberUiToRawMap = useMemo(
    () => {
      const map = {};
      modemNumberFilterOptions.forEach((option) => {
        map[option.value] = option.rawValue;
      });
      Object.values(modemNumberFilterOptionsByServer).forEach((options) => {
        options.forEach((option) => {
          map[option.value] = option.rawValue;
        });
      });
      return map;
    },
    [modemNumberFilterOptions, modemNumberFilterOptionsByServer]
  );

  const modemNumberUiToServerIdMap = useMemo(
    () => {
      const map = {};
      Object.values(modemNumberFilterOptionsByServer).forEach((options) => {
        options.forEach((option) => {
          const serverIdPart = String(option.value || '').split('__').pop();
          const parsedServerId = Number(serverIdPart);
          if (Number.isFinite(parsedServerId)) {
            map[option.value] = String(parsedServerId);
          }
        });
      });
      return map;
    },
    [modemNumberFilterOptionsByServer]
  );

  const modemNumberTableFilterOptions = useMemo(
    () => connections.flatMap((conn) =>
      (conn.modems || []).map((modem) => ({
        value: `${conn.name}__${modem.id}`,
        label: `№ ${getConnectionModemNumber(modem)} ${modem.name || ''}`.trim(),
        rawValue: String(getConnectionModemNumber(modem)),
        serverName: conn.name,
      }))
    ),
    [connections]
  );

  const modemNumberUiValuesByServerAndRaw = useMemo(
    () => modemNumberTableFilterOptions.reduce((acc, option) => {
      const key = `${option.serverName || ''}__${option.rawValue || ''}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(option.value);
      return acc;
    }, {}),
    [modemNumberTableFilterOptions]
  );

  const phoneNumberFilterOptions = useMemo(
    () => (filterOptions.phone_numbers || []).map((phone) => {
      const serverName = phone.server_name || '';
      const modemRaw = phone.modem_number != null ? String(phone.modem_number) : '';
      const modemUiValues = modemNumberUiValuesByServerAndRaw[`${serverName}__${modemRaw}`] || [];
      return {
        value: phone.value,
        label: phone.label,
        serverName,
        modemRawValue: modemRaw,
        modemUiValues,
      };
    }),
    [filterOptions.phone_numbers, modemNumberUiValuesByServerAndRaw]
  );

  // Resize handler
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Загрузка данных
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Формируем query параметры
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: itemsPerPage.toString(),
        sort_by: sortConfig.key,
        sort_order: sortConfig.direction,
      });

      // Добавляем фильтры
      if (filters.sim_status) params.append('sim_status', filters.sim_status);
      if (filters.operator) params.append('operator', filters.operator);
      if (filters.geo) params.append('geo', filters.geo);
      if (filters.server) params.append('server', filters.server);
      if (filters.modem_number) params.append('modem_number', filters.modem_number);
      if (filters.modem_server_ids) params.append('modem_server_ids', filters.modem_server_ids);
      if (filters.phone_number) params.append('phone_number', filters.phone_number);
      if (filters.amount) params.append('amount', filters.amount);
      if (filters.registered_by) params.append('registered_by', filters.registered_by);
      if (filters.personal_account_owner) params.append('personal_account_owner', filters.personal_account_owner);
      if (filters.payment_filter) params.append('payment_filter', filters.payment_filter);
      if (filters.last_payment_from) params.append('last_payment_from', filters.last_payment_from);
      if (filters.last_payment_to) params.append('last_payment_to', filters.last_payment_to);
      if (filters.next_payment_from) params.append('next_payment_from', filters.next_payment_from);
      if (filters.next_payment_to) params.append('next_payment_to', filters.next_payment_to);
      if (filters.next_payment_urgency) params.append('next_payment_urgency', filters.next_payment_urgency);
      if (filters.freeze_status) params.append('freeze_status', filters.freeze_status);
      if (filters.freeze_until_from) params.append('freeze_until_from', filters.freeze_until_from);
      if (filters.freeze_until_to) params.append('freeze_until_to', filters.freeze_until_to);
      if (filters.comment) params.append('comment', filters.comment);
      if (filters.imei) params.append('imei', filters.imei);
      if (filters.mac_address) params.append('mac_address', filters.mac_address);
      if (filters.imei_mismatch) params.append('imei_mismatch', 'true');

      // modem_status всегда фильтруем на backend по всей таблице.
      // Приоритет: фильтр колонки таблицы > верхние чипы статуса.
      if (filters.modem_status) {
        params.append('modem_status', filters.modem_status);
      } else {
        const headerModemStatus = resolveHeaderModemStatusFilter(activeStatus);
        if (headerModemStatus) {
          params.append('modem_status', headerModemStatus);
        }
      }

      const response = await fetchApi(`/api/sim-cards?${params.toString()}`);
      setSimData(response.items.map((item) => transformSimData(item)));
      setTotalItems(response.total);
      setTotalPages(response.total_pages);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, sortConfig, filters, activeStatus]);

  // Загрузка статистики
  const loadStats = useCallback(async () => {
    try {
      const response = await fetchApi('/api/sim-cards/stats');
      setStats(response);
    } catch (err) {
      // Статистика не критична, не показываем ошибку
    }
  }, []);

  // Загрузка опций фильтров
  const loadFilterOptions = useCallback(async () => {
    try {
      const response = await fetchApi('/api/sim-cards/filter-options');
      setFilterOptions(response);
    } catch (err) {
      // Опции не критичны
    }
  }, []);

  // Загрузка подключений с модемами (для инлайн-выбора модема)
  const loadConnections = useCallback(async () => {
    try {
      const response = await fetchApi('/api/sim-cards/connections');
      setConnections(response.connections || []);
    } catch (err) {
      // Подключения не критичны
    }
  }, []);

  const loadOperatorTemplatesForLk = useCallback(async () => {
    try {
      const root = await fetchApi('/api/settings/operator-templates');
      const templates =
        root?.data?.data?.templates ||
        root?.data?.templates ||
        [];
      setOperatorTemplatesForLk(Array.isArray(templates) ? templates : []);
    } catch (_err) {
      setOperatorTemplatesForLk([]);
    }
  }, []);

  // Начальная загрузка
  useEffect(() => {
    loadData();
    loadStats();
    loadFilterOptions();
    loadConnections();
    loadOperatorTemplatesForLk();
  }, [loadData, loadStats, loadFilterOptions, loadConnections, loadOperatorTemplatesForLk]);

  // Вычисляемые значения для хедера: счётчики модемов как на Dashboard (видимые модемы),
  // а не количество SIM-карт с таким статусом модема.
  const globalModemStatus = stats.global_modem_status || {};
  const statusCounts = {
    all: stats.total,
    available: globalModemStatus.available ?? stats.by_modem_status?.available ?? 0,
    unavailable: globalModemStatus.unavailable
      ?? stats.by_modem_status?.unavailable
      ?? 0,
    unknown: globalModemStatus.unknown ?? stats.by_modem_status?.unknown ?? 0,
  };

  const simReserves = [
    { id: 'msk', label: 'Резерв Мск', count: stats.reserves?.msk || 0 },
    { id: 'spb', label: 'Резерв СПб', count: stats.reserves?.spb || 0 },
  ];

  const totalPayments = stats.total_payments?.toLocaleString('ru-RU') || '0';
  const avgTariffPrice = stats.avg_tariff_price?.toLocaleString('ru-RU', { minimumFractionDigits: 2 }) || '0';

  // Обработчики хедера
  const handleStatusChange = useCallback((status) => {
    setActiveStatus(status);
    // Сбрасываем sim_status (от резервов) и modem_status (от панели фильтров) чтобы не было конфликтов
    setFilters((prev) => {
      const next = { ...prev };
      delete next.sim_status;
      delete next.modem_status;
      return next;
    });
    setCurrentPage(1);
  }, []);

  const handleSimReserveClick = useCallback((reserveId) => {
    // Фильтрация по резерву — сбрасываем статус модема, чтобы показать все SIM с этим sim_status
    setActiveStatus('all');
    setFilters((prev) => {
      const next = { ...prev };
      delete next.modem_status;
      next.sim_status = `reserve_${reserveId}`;
      return next;
    });
    setCurrentPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    loadData();
    loadStats();
  }, [loadData, loadStats]);

  const handleFilter = useCallback(() => {
    setIsFilterPanelVisible((prev) => {
      const next = !prev;
      if (next) {
        // Закрываем инфо-панель при открытии фильтров
        setIsInfoPanelVisible(false);
        setInfoRowId(null);
        setActiveRowId(null);
      }
      return next;
    });
  }, []);

  const handleFilterPanelServerDraftChange = useCallback(async (selectedServers = []) => {
    const normalizedServers = (selectedServers || []).filter(Boolean);
    const requestId = scopedPanelRequestIdRef.current + 1;
    scopedPanelRequestIdRef.current = requestId;

    if (normalizedServers.length === 0) {
      setScopedPanelStats(null);
      return;
    }

    try {
      const params = new URLSearchParams({
        server: normalizedServers.join(','),
      });
      const response = await fetchApi(`/api/sim-cards/scoped-panel-stats?${params.toString()}`);

      if (scopedPanelRequestIdRef.current !== requestId) {
        return;
      }

      setScopedPanelStats(response || null);
    } catch (err) {
      if (scopedPanelRequestIdRef.current !== requestId) {
        return;
      }
      setScopedPanelStats(null);
    }
  }, []);

  // Применение фильтров
  const handleFilterApply = useCallback((newFilters) => {
    // Преобразуем фильтры из панели фильтров в формат API
    const apiFilters = {};

    if (newFilters.modemStatus?.length > 0) {
      apiFilters.modem_status = newFilters.modemStatus.join(',');
    }
    if (newFilters.simStatus?.length > 0) {
      apiFilters.sim_status = newFilters.simStatus.join(',');
    }
    if (newFilters.geo?.length > 0) {
      apiFilters.geo = newFilters.geo.join(',');
    }
    if (newFilters.payment?.length > 0) {
      apiFilters.payment_filter = newFilters.payment[0];
    }
    if (newFilters.server?.length > 0) {
      apiFilters.server = newFilters.server.join(',');
    }
    if (newFilters.modemNumber?.length > 0) {
      const modemNumbersRaw = [...new Set(
        newFilters.modemNumber
          .map((value) => modemNumberUiToRawMap[value] || value)
          .filter(Boolean)
      )];
      const modemServerIds = [...new Set(
        newFilters.modemNumber
          .map((value) => modemNumberUiToServerIdMap[value])
          .filter(Boolean)
      )];
      if (modemNumbersRaw.length > 0) {
        apiFilters.modem_number = modemNumbersRaw.join(',');
      }
      if (modemServerIds.length > 0) {
        apiFilters.modem_server_ids = modemServerIds.join(',');
      }
    }
    if (newFilters.phoneNumber?.length > 0) {
      apiFilters.phone_number = newFilters.phoneNumber.join(',');
    }
    if (newFilters.registeredBy?.length > 0) {
      apiFilters.registered_by = newFilters.registeredBy.join(',');
    }
    if (newFilters.personalAccountOwner?.length > 0) {
      apiFilters.personal_account_owner = newFilters.personalAccountOwner.join(',');
    }
    if (newFilters.operator?.length > 0) {
      apiFilters.operator = newFilters.operator.join(',');
    }

    setFilters(apiFilters);
    setCurrentPage(1);
  }, [modemNumberUiToRawMap, modemNumberUiToServerIdMap]);

  // Сброс фильтров
  const handleFilterReset = useCallback(() => {
    setFilters({});
    setCurrentPage(1);
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = Object.values(filters).filter((v) => v !== undefined && v !== null && v !== '').length;
    if (activeStatus !== 'all') count++;
    return count;
  }, [filters, activeStatus]);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    setActiveStatus('all');
    setCurrentPage(1);
    setTableFiltersResetToken((prev) => prev + 1);
    setSuccessMessage(null);
  }, []);

  const handleImeiMismatchCheck = useCallback(() => {
    if (filters.imei_mismatch) {
      setFilters((prev) => {
        const next = { ...prev };
        delete next.imei_mismatch;
        return next;
      });
      setCurrentPage(1);
      setSuccessMessage(null);
      return;
    }
    setFilters((prev) => ({ ...prev, imei_mismatch: true }));
    setCurrentPage(1);
  }, [filters.imei_mismatch]);

  // После загрузки с фильтром IMEI mismatch — показать результат
  useEffect(() => {
    if (!filters.imei_mismatch || isLoading) {
      return;
    }
    if (totalItems === 0) {
      setSuccessMessage('Расхождений IMEI не найдено');
      return;
    }
    setSuccessMessage(`Найдено расхождений IMEI: ${totalItems}`);
  }, [filters.imei_mismatch, totalItems, isLoading]);

  // Массовое удаление выбранных SIM-карт
  const handleBulkDelete = useCallback(async () => {
    if (selectedRows.length === 0) return;

    const confirmed = window.confirm(
      `Удалить ${selectedRows.length} SIM-карт(ы)?`
    );
    if (!confirmed) return;

    try {
      await fetchApi('/api/sim-cards/bulk/delete', {
        method: 'DELETE',
        body: JSON.stringify({ ids: selectedRows }),
      });
      loadData();
      loadStats();
      setSelectedRows([]);
    } catch (err) {
      // Fallback: удаляем по одной
      try {
        await Promise.all(
          selectedRows.map((id) =>
            fetchApi(`/api/sim-cards/${id}`, { method: 'DELETE' })
          )
        );
        loadData();
        loadStats();
        setSelectedRows([]);
      } catch (innerErr) {
        setError(`Ошибка удаления: ${innerErr.message}`);
      }
    }
  }, [selectedRows, loadData, loadStats]);

  const downloadBlobFile = useCallback((blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, []);

  const parseExportErrorDetail = useCallback(async (response, fallbackMessage) => {
    const errData = await response.json().catch(() => ({}));
    let detail = errData.detail;
    if (Array.isArray(detail)) {
      detail = detail
        .map((item) => (item && typeof item === 'object' && item.msg ? item.msg : String(item)))
        .join('; ');
    } else if (detail != null && typeof detail === 'object') {
      detail = JSON.stringify(detail);
    }
    return detail || fallbackMessage;
  }, []);

  const handleExport = useCallback(async () => {
    try {
      setError(null);
      const token = getAuthToken();
      // Как ЕСИА: пустой ids — все активные, иначе только выделенные.
      const response = await fetch(`${API_BASE_URL}/api/sim-cards/export/xlsx`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: selectedRows }),
      });

      if (!response.ok) {
        throw new Error(await parseExportErrorDetail(response, 'Ошибка экспорта'));
      }

      const blob = await response.blob();
      const suffix = selectedRows.length > 0 ? '_selected' : '';
      downloadBlobFile(
        blob,
        `sim_cards${suffix}_${new Date().toISOString().split('T')[0]}.xlsx`,
      );
    } catch (err) {
      setError(`Ошибка экспорта: ${err.message}`);
    }
  }, [selectedRows, downloadBlobFile, parseExportErrorDetail]);

  const handleExportEsia = useCallback(async () => {
    try {
      setError(null);
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/sim-cards/export/esia`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: selectedRows }),
      });

      if (!response.ok) {
        throw new Error(await parseExportErrorDetail(response, 'Ошибка экспорта ЕСИА'));
      }

      const blob = await response.blob();
      downloadBlobFile(blob, `esia_devices_${new Date().toISOString().split('T')[0]}.xls`);
    } catch (err) {
      setError(`Ошибка экспорта ЕСИА: ${err.message}`);
    }
  }, [selectedRows, downloadBlobFile, parseExportErrorDetail]);

  const handleAdd = useCallback(() => {
    setIsAddPanelOpen(true);
  }, []);

  // Закрытие панели добавления
  const handleAddPanelClose = useCallback(() => {
    setIsAddPanelOpen(false);
  }, []);

  // Сохранение новой SIM-карты
  const handleAddPanelSave = useCallback(async (newSimData) => {
    try {
      // Обработка загрузки файла — отправляем на эндпоинт импорта Excel
      if (newSimData?.type === 'file_upload') {
        const token = getAuthToken();
        const formData = new FormData();
        formData.append('file', newSimData.file);

        const response = await fetch(`${API_BASE_URL}/api/sim-cards/import/xlsx`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.detail || `Ошибка импорта (${response.status})`);
        }

        const result = await response.json();
        const parts = [];
        if (result.created > 0) parts.push(`создано: ${result.created}`);
        if (result.updated > 0) parts.push(`обновлено: ${result.updated}`);
        if (result.skipped > 0) parts.push(`пропущено: ${result.skipped}`);
        if (result.errors?.length > 0) parts.push(`ошибок: ${result.errors.length}`);
        setSuccessMessage(`Импорт завершён — ${parts.join(', ') || 'без изменений'}`);

        loadData();
        loadStats();
        setIsAddPanelOpen(false);
        return;
      }

      let personalAccountId = newSimData.personalAccountId || null;

      if (newSimData.newPersonalAccount) {
        const paResponse = await fetchApi('/api/personal-accounts', {
          method: 'POST',
          body: JSON.stringify(newSimData.newPersonalAccount),
        });
        personalAccountId = paResponse.id;
      }

      const baseApiData = {
        sim_status: newSimData.simStatus || 'free',
        operator: newSimData.operator || null,
        geo: newSimData.geo || null,
        amount: parseFloat(newSimData.amount) || 0,
        last_payment_date: newSimData.paymentDate || null,
        next_payment_date: newSimData.paymentDate || null,
        registered_by: newSimData.registeredBy || null,
        account_link: newSimData.accountLink || null,
        comment: newSimData.comment || null,
        imei: newSimData.imei?.trim() || null,
        mac_address: newSimData.mac?.trim() || null,
        kraken_server_id: newSimData.modem ? parseInt(newSimData.modem) : null,
        personal_account_id: personalAccountId,
        server_option: newSimData.serverOption || null,
        modem_option: newSimData.modemOption || null,
      };

      const phones = parseSimPhoneNumbersInput(newSimData.phoneNumber);
      const bodies =
        phones.length === 0
          ? [{ ...baseApiData, phone_number: null }]
          : phones.map((phone) => ({ ...baseApiData, phone_number: phone }));

      for (const body of bodies) {
        await fetchApi('/api/sim-cards', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }

      if (phones.length > 1) {
        setSuccessMessage(`Добавлено сим-карт: ${phones.length}`);
      }

      // Обновляем данные
      loadData();
      loadStats();
      setIsAddPanelOpen(false);
    } catch (err) {
      setError(`Ошибка добавления: ${err.message}`);
    }
  }, [loadData, loadStats]);

  // Обработчик редактирования (открытие панели)
  const handleEdit = useCallback(() => {
    if (selectedRows.length > 0) {
      setIsEditPanelOpen(true);
    }
  }, [selectedRows]);

  // Закрытие панели редактирования
  const handleEditPanelClose = useCallback(() => {
    setIsEditPanelOpen(false);
  }, []);

  // Сохранение изменений из панели редактирования
  const handleEditPanelSave = useCallback(async (changedData, items) => {
    try {
      let personalAccountId = changedData.personalAccountId || null;

      if (changedData.newPersonalAccount) {
        const paResponse = await fetchApi('/api/personal-accounts', {
          method: 'POST',
          body: JSON.stringify(changedData.newPersonalAccount),
        });
        personalAccountId = paResponse.id;
      }

      const apiData = {};
      if (Object.prototype.hasOwnProperty.call(changedData, 'simStatus')) {
        apiData.sim_status = changedData.simStatus || null;
      }
      if (Object.prototype.hasOwnProperty.call(changedData, 'operator')) {
        apiData.operator = normalizeOperatorValue(changedData.operator);
      }
      if (Object.prototype.hasOwnProperty.call(changedData, 'geo')) {
        apiData.geo = changedData.geo || null;
      }
      if (Object.prototype.hasOwnProperty.call(changedData, 'modem')) {
        apiData.kraken_server_id = changedData.modem ? parseInt(changedData.modem, 10) : null;
      }
      if (Object.prototype.hasOwnProperty.call(changedData, 'server_option')) {
        apiData.server_option = changedData.server_option;
      }
      if (Object.prototype.hasOwnProperty.call(changedData, 'modem_option')) {
        apiData.modem_option = changedData.modem_option;
      }
      if (changedData.phoneNumber !== undefined) apiData.phone_number = changedData.phoneNumber;
      if (changedData.amount !== undefined) apiData.amount = parseFloat(changedData.amount);
      if (Object.prototype.hasOwnProperty.call(changedData, 'lastPayment')) {
        apiData.last_payment_date = changedData.lastPayment || null;
      }
      if (Object.prototype.hasOwnProperty.call(changedData, 'nextPayment')) {
        apiData.next_payment_date = changedData.nextPayment || null;
      }
      if (Object.prototype.hasOwnProperty.call(changedData, 'freezeUntil')) {
        apiData.freeze_until = changedData.freezeUntil || null;
      }
      if (Object.prototype.hasOwnProperty.call(changedData, 'registeredBy')) {
        apiData.registered_by = changedData.registeredBy || null;
      }
      if (changedData.comment !== undefined) apiData.comment = changedData.comment;
      if (Object.prototype.hasOwnProperty.call(changedData, 'imei')) {
        apiData.imei = changedData.imei?.trim() || null;
      }
      if (Object.prototype.hasOwnProperty.call(changedData, 'mac')) {
        apiData.mac_address = changedData.mac?.trim() || null;
      }
      if (Object.prototype.hasOwnProperty.call(changedData, 'personalAccountId') || changedData.newPersonalAccount) {
        apiData.personal_account_id = personalAccountId;
      }

      if (Object.keys(apiData).length === 0) {
        setIsEditPanelOpen(false);
        return;
      }

      // Массовое обновление
      await fetchApi('/api/sim-cards/bulk/update', {
        method: 'PUT',
        body: JSON.stringify({
          ids: selectedRows,
          data: apiData,
        }),
      });

      // Обновляем данные
      loadData();
      loadStats();
      setIsEditPanelOpen(false);
      setSelectedRows([]);
    } catch (err) {
      setError(`Ошибка обновления: ${err.message}`);
    }
  }, [selectedRows, loadData, loadStats]);

  // Инлайн-редактирование ячейки таблицы
  const handleCellEdit = useCallback(async (rowId, columnKey, newValue) => {
    try {
      // Смена подключения (сервера) — особая логика
      if (columnKey === 'server') {
        if (newValue === '' || newValue == null) {
          await fetchApi(`/api/sim-cards/${rowId}`, {
            method: 'PUT',
            body: JSON.stringify({
              geo: null,
              kraken_server_id: null,
              server_option: null,
              modem_option: null,
            }),
          });
          loadData();
          loadStats();
          return;
        }

        const conn = connections.find((c) => c.name === newValue);
        if (!conn) return;

        const body = {
          geo: conn.city || '',
          server_option: conn.name,
          kraken_server_id: null,
          modem_option: null,
        };

        await fetchApi(`/api/sim-cards/${rowId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });

        loadData();
        loadStats();
        return;
      }

      // Изменение последнего платежа → автопересчёт следующего (+30 дней)
      if (columnKey === 'lastPayment') {
        const body = { last_payment_date: newValue || null };
        if (newValue) {
          const lastDate = new Date(newValue);
          lastDate.setDate(lastDate.getDate() + 30);
          body.next_payment_date = lastDate.toISOString().split('T')[0];
        }
        await fetchApi(`/api/sim-cards/${rowId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        loadData();
        loadStats();
        return;
      }

      const keyMap = {
        modem: 'kraken_server_id',
        modemNumber: 'kraken_server_id',
        sim: 'sim_status',
        operator: 'operator',
        phoneNumber: 'phone_number',
        amount: 'amount',
        nextPayment: 'next_payment_date',
        freezeUntil: 'freeze_until',
        registeredBy: 'registered_by',
        registeredOn: 'registered_by',
        comment: 'comment',
        geo: 'geo',
      };

      const apiKey = keyMap[columnKey];
      if (!apiKey) return;

      let apiValue = newValue;
      let modemOptionLabel = null;
      if (columnKey === 'amount') {
        apiValue = parseFloat(newValue) || 0;
      } else if (columnKey === 'modem' || columnKey === 'modemNumber') {
        apiValue = newValue ? parseInt(newValue, 10) : null;
        if (apiValue != null) {
          for (const c of connections) {
            for (const modem of c.modems || []) {
              if (modem.id !== apiValue) {
                continue;
              }
              const modemNumber = getConnectionModemNumber(modem);
              modemOptionLabel = modemNumber != null ? `№ ${modemNumber}` : null;
              break;
            }
            if (modemOptionLabel) {
              break;
            }
          }
        }
      } else if (columnKey === 'geo') {
        apiValue = newValue ? String(newValue).trim() : null;
      } else if (columnKey === 'nextPayment') {
        apiValue = newValue || null;
      } else if (columnKey === 'freezeUntil') {
        // YYYY-MM-DD — Pydantic принимает как datetime (полночь)
        apiValue = newValue || null;
      } else if (columnKey === 'operator') {
        apiValue = normalizeOperatorValue(newValue);
      }

      const putBody =
        columnKey === 'modem' || columnKey === 'modemNumber'
          ? { [apiKey]: apiValue, modem_option: modemOptionLabel }
          : { [apiKey]: apiValue };

      await fetchApi(`/api/sim-cards/${rowId}`, {
        method: 'PUT',
        body: JSON.stringify(putBody),
      });

      loadData();
      loadStats();
    } catch (err) {
      setError(`Ошибка обновления: ${err.message}`);
    }
  }, [loadData, loadStats, connections]);

  const handleMarkPaid = useCallback((row) => {
    setPaymentConfirmRow(row);
  }, []);

  const handleConfirmPayment = useCallback(async () => {
    if (!paymentConfirmRow) return;
    const rowId = paymentConfirmRow.id;
    setPaymentConfirmRow(null);

    try {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const next = new Date(today);
      next.setDate(next.getDate() + 30);
      const nextStr = next.toISOString().split('T')[0];

      await fetchApi(`/api/sim-cards/${rowId}`, {
        method: 'PUT',
        body: JSON.stringify({
          last_payment_date: todayStr,
          next_payment_date: nextStr,
        }),
      });

      loadData();
      loadStats();
    } catch (err) {
      setError(`Ошибка обновления оплаты: ${err.message}`);
    }
  }, [paymentConfirmRow, loadData, loadStats]);

  // Опции для select-полей инлайн-редактирования
  const tableEditOptions = React.useMemo(() => {
    const citySet = new Set();
    connections.forEach((c) => {
      if (c.city && String(c.city).trim()) {
        citySet.add(String(c.city).trim());
      }
    });
    const geoFromConnections = Array.from(citySet).sort((a, b) => a.localeCompare(b, 'ru'));

    return {
    sim: SIM_STATUS_OPTIONS,
    operator: OPERATOR_OPTIONS,
    geo: [
      { value: '', label: '— Без гео —' },
      ...geoFromConnections.map((city) => ({ value: city, label: city })),
    ],
    // Сервер — «без сервера» + все подключения (имя + город)
    server: [
      { value: '', label: '— Без сервера —' },
      ...connections.map((conn) => ({
        value: conn.name,
        label: `${conn.name} (${conn.city || '—'})`,
      })),
    ],
    // Модемы — все, с привязкой к подключению (для фильтрации по row.server)
    modem: connections.flatMap((conn) =>
      (conn.modems || []).map((m) => ({
        value: String(m.id),
        label: `№ ${getConnectionModemNumber(m)} ${m.name}`,
        modemNumber: getConnectionModemNumber(m),
        name: m.name,
        connectionName: conn.name,
      }))
    ),
    };
  }, [connections]);

  // Обработчики таблицы
  const handleSort = useCallback((newSortConfig) => {
    // Маппинг ключей таблицы к ключам API
    const keyMap = {
      modem: 'id',
      sim: 'sim_status',
      freezeUntil: 'freeze_until',
      geo: 'geo',
      server: 'kraken_server_id',
      modemNumber: 'kraken_server_id',
      phoneNumber: 'phone_number',
      operator: 'operator',
      amount: 'amount',
      lastPayment: 'last_payment_date',
      nextPayment: 'next_payment_date',
      registeredBy: 'registered_by',
      registeredOn: 'registered_by',
    };

    setSortConfig({
      key: keyMap[newSortConfig.key] || newSortConfig.key,
      direction: newSortConfig.direction,
    });
    setCurrentPage(1);
  }, []);

  const handleTableFilter = useCallback((columnKey, filterValue) => {
    // Табличные фильтры должны работать глобально и не конфликтовать с верхними статус-чипами.
    setActiveStatus('all');

    // Фильтрация по столбцу
    const keyMap = {
      modem: 'modem_status',
      sim: 'sim_status',
      geo: 'geo',
      server: 'server',
      modemNumber: 'modem_number',
      phoneNumber: 'phone_number',
      operator: 'operator',
      amount: 'amount',
      registeredBy: 'personal_account_owner',
      registeredOn: 'registered_by',
      comment: 'comment',
      imei: 'imei',
      mac: 'mac_address',
    };

    // Обработка фильтров по диапазону дат
    if (columnKey === 'lastPayment' || columnKey === 'nextPayment' || columnKey === 'freezeUntil') {
      const prefix = columnKey === 'lastPayment'
        ? 'last_payment'
        : columnKey === 'nextPayment'
          ? 'next_payment'
          : 'freeze_until';
      const parts = filterValue ? filterValue.split(',') : [];
      let fromVal = null;
      let toVal = null;
      const colors = [];
      parts.forEach((p) => {
        if (p.startsWith('from:')) fromVal = p.slice(5);
        if (p.startsWith('to:')) toVal = p.slice(3);
        if (p.startsWith('color:')) colors.push(p.slice(6));
      });

      setFilters((prev) => {
        const next = { ...prev };
        if (fromVal) {
          next[`${prefix}_from`] = fromVal;
        } else {
          delete next[`${prefix}_from`];
        }
        if (toVal) {
          next[`${prefix}_to`] = toVal;
        } else {
          delete next[`${prefix}_to`];
        }
        // Срочность оплаты — серверный фильтр (только для nextPayment)
        if (columnKey === 'nextPayment') {
          if (colors.length > 0) {
            next.next_payment_urgency = colors.join(',');
          } else {
            delete next.next_payment_urgency;
          }
        }
        return next;
      });
      setCurrentPage(1);
      return;
    }

    const apiKey = keyMap[columnKey];
    if (!apiKey) return;

    let normalizedFilterValue = filterValue;
    if (apiKey === 'modem_status' && filterValue) {
      const normalizedStatuses = filterValue
        .split(',')
        .map((value) => normalizeModemStatusKey(value))
        .filter(Boolean);
      normalizedFilterValue = normalizedStatuses.length > 0
        ? normalizedStatuses.join(',')
        : null;
    }
    if (apiKey === 'modem_number') {
      const filterParts = filterValue
        ? filterValue.split(',').filter(Boolean)
        : [];
      const modemNumbersRaw = [...new Set(
        filterParts
          .map((value) => modemNumberUiToRawMap[value] || value)
          .filter(Boolean)
      )];
      const modemServerIds = [...new Set(
        filterParts
          .map((value) => modemNumberUiToServerIdMap[value])
          .filter(Boolean)
      )];
      normalizedFilterValue = modemNumbersRaw.length > 0 ? modemNumbersRaw.join(',') : null;
      setFilters((prev) => {
        const next = { ...prev };
        if (modemServerIds.length > 0) {
          next.modem_server_ids = modemServerIds.join(',');
        } else {
          delete next.modem_server_ids;
        }
        if (normalizedFilterValue) {
          next[apiKey] = normalizedFilterValue;
        } else {
          delete next[apiKey];
        }
        return next;
      });
      setCurrentPage(1);
      return;
    }

    if (normalizedFilterValue) {
      setFilters((prev) => ({
        ...prev,
        [apiKey]: normalizedFilterValue,
      }));
    } else {
      // Сброс фильтра — удаляем ключ
      setFilters((prev) => {
        const next = { ...prev };
        delete next[apiKey];
        return next;
      });
    }
    setCurrentPage(1);
  }, [modemNumberUiToRawMap, modemNumberUiToServerIdMap]);

  const handleRowSelect = useCallback((rowId) => {
    setSelectedRows((prev) => {
      if (prev.includes(rowId)) {
        return prev.filter((id) => id !== rowId);
      }
      return [...prev, rowId];
    });
  }, []);

  const handleBulkSelect = useCallback((ids, shouldSelect) => {
    setSelectedRows((prev) => {
      if (shouldSelect) {
        const combined = new Set([...prev, ...ids]);
        return [...combined];
      }
      const removeSet = new Set(ids);
      return prev.filter((id) => !removeSet.has(id));
    });
  }, []);

  const handleAllSelect = useCallback(() => {
    setSelectedRows((prev) => {
      if (prev.length === simData.length) {
        return [];
      }
      return simData.map((row) => row.id);
    });
  }, [simData]);

  const getUniqueModemIds = useCallback((rows) => {
    return [...new Set(
      (rows || [])
        .map((row) => Number(row?._raw?.kraken_server_id))
        .filter((id) => Number.isFinite(id) && id > 0)
    )];
  }, []);

  const runProxyCheckForModems = useCallback(async (modemIds, sourceLabel = 'выбранных SIM') => {
    if (proxyCheckLoading) {
      return;
    }

    if (!Array.isArray(modemIds) || modemIds.length === 0) {
      setError('Не удалось определить модемы для проверки прокси');
      return;
    }

    setProxyCheckLoading(true);
    setError(null);

    try {
      const responses = await Promise.allSettled(
        modemIds.map((modemId) =>
          fetchApi(`/api/proxy-checker/quick-check-modem/${modemId}?only_unavailable=true`, {
            method: 'POST',
          })
        )
      );

      const successful = responses
        .filter((item) => item.status === 'fulfilled')
        .map((item) => item.value?.data || {});
      const failed = responses.filter((item) => item.status === 'rejected');

      const totalChecked = successful.reduce((sum, item) => sum + Number(item.total_checked || 0), 0);
      const totalAvailable = successful.reduce((sum, item) => sum + Number(item.available || 0), 0);
      const totalUnavailable = successful.reduce((sum, item) => sum + Number(item.unavailable || 0), 0);
      const totalErrors = successful.reduce((sum, item) => sum + Number(item.errors || 0), 0);

      setSuccessMessage(
        `Проверка прокси (${sourceLabel}): модемов ${modemIds.length}, ` +
        `проверено ${totalChecked}, доступно ${totalAvailable}, ` +
        `недоступно ${totalUnavailable}, ошибок ${totalErrors}`
      );

      if (failed.length > 0) {
        setError(`Часть модемов не удалось проверить: ${failed.length} из ${modemIds.length}`);
      }

      loadData();
      loadStats();
    } catch (err) {
      setError(`Ошибка проверки прокси: ${err.message}`);
    } finally {
      setProxyCheckLoading(false);
    }
  }, [loadData, loadStats, proxyCheckLoading]);

  const handleBulkProxyCheck = useCallback(() => {
    const selectedItems = simData.filter((item) => selectedRows.includes(item.id));
    const modemIds = getUniqueModemIds(selectedItems);
    runProxyCheckForModems(modemIds, 'выбранных SIM');
  }, [simData, selectedRows, getUniqueModemIds, runProxyCheckForModems]);

  const handleActionClick = useCallback((row, action) => {
    if (action === 'delete') {
      // Удаление одной SIM
      fetchApi(`/api/sim-cards/${row.id}`, { method: 'DELETE' })
        .then(() => {
          loadData();
          loadStats();
        })
        .catch((err) => setError(`Ошибка удаления: ${err.message}`));
    } else if (action === 'edit') {
      // Редактирование одной SIM — выделяем строку и открываем панель
      setSelectedRows([row.id]);
      setIsEditPanelOpen(true);
    } else if (action === 'info') {
      // Переключение инфо-панели: повторный клик по той же строке — закрыть
      setInfoRowId((prevId) => {
        if (prevId === row.id) {
          setIsInfoPanelVisible(false);
          setActiveRowId(null);
          return null;
        }
        setIsInfoPanelVisible(true);
        setActiveRowId(row.id);
        setIsFilterPanelVisible(false);
        return row.id;
      });
    } else if (action === 'checkProxy') {
      const modemIds = getUniqueModemIds([row]);
      runProxyCheckForModems(modemIds, 'SIM');
    }
  }, [loadData, loadStats, getUniqueModemIds, runProxyCheckForModems]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  }, []);

  const filteredSimData = useMemo(() => simData, [simData]);

  // Опции фильтров для столбцов таблицы (из API — все страницы)
  const columnFilterOptions = useMemo(() => {
    const ms = filterOptions.modem_statuses || {};
    const globalModem = stats.global_modem_status || {};
    const hasGlobalModemCounts = stats.global_modem_status != null;
    const safeCount = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const pickFirstPositiveOrDefinedCount = (...values) => {
      const normalized = values
        .filter((value) => value !== undefined && value !== null)
        .map((value) => safeCount(value));
      const positive = normalized.find((value) => value > 0);
      if (positive !== undefined) {
        return positive;
      }
      return normalized.length > 0 ? normalized[0] : 0;
    };

    const unavailableFromFilters = safeCount(ms.unavailable)
      + safeCount(ms.not_working)
      + safeCount(ms.offline);
    const modemStatusOpts = [
      {
        value: 'available',
        label: 'Доступен',
        count: hasGlobalModemCounts
          ? safeCount(globalModem.available)
          : pickFirstPositiveOrDefinedCount(
            ms.available,
            ms.working,
            stats.by_modem_status?.available,
          ),
      },
      {
        value: 'unavailable',
        label: 'Недоступен',
        count: hasGlobalModemCounts
          ? safeCount(globalModem.unavailable)
          : (unavailableFromFilters > 0
            ? unavailableFromFilters
            : pickFirstPositiveOrDefinedCount(stats.by_modem_status?.unavailable)),
      },
      {
        value: 'unknown',
        label: 'Неизвестно',
        count: hasGlobalModemCounts
          ? safeCount(globalModem.unknown)
          : pickFirstPositiveOrDefinedCount(
            ms.unknown,
            stats.by_modem_status?.unknown,
          ),
      },
      {
        value: 'disabled',
        label: 'Отключен',
        count: pickFirstPositiveOrDefinedCount(
          ms.disabled,
          stats.by_modem_status?.disabled,
        ),
      },
    ];

    const row72FromApi = (filterOptions.sim_special_filters || []).find(
      (s) => s.value === SIM_FILTER_MODEM_72H_TOKEN
    );
    // Строка всегда в UI: даже без поля sim_special_filters в ответе API (старый бэкенд / кэш)
    const simStatusOpts = [
      ...(filterOptions.sim_statuses || []).map((s) => ({
        value: s.value,
        label: SIM_STATUS_LABELS[s.value] || s.label,
        count: s.count,
      })),
      {
        value: SIM_FILTER_MODEM_72H_TOKEN,
        label: row72FromApi?.label || SIM_FILTER_MODEM_72H_LABEL,
        count: row72FromApi != null ? Number(row72FromApi.count) || 0 : 0,
      },
    ];

    const geoOpts = (filterOptions.geos || []).map((g) => ({
      value: g.value,
      label: shortenGeo(g.value),
      count: g.count,
    }));

    const serverOpts = (filterOptions.servers || []).map((s) => ({
      value: s.value,
      label: s.label,
      count: s.count,
    }));

    const modemNumberOpts = modemNumberTableFilterOptions.length > 0
      ? modemNumberTableFilterOptions
      : modemNumberFilterOptions;

    const phoneNumberOpts = phoneNumberFilterOptions;

    const operatorOpts = (filterOptions.operators || []).map((o) => ({
      value: o.value,
      label: o.label,
      count: o.count,
    }));

    const amountOpts = (filterOptions.amounts || []).map((a) => ({
      value: String(a.value),
      label: a.label,
      count: a.count,
    }));

    const registeredByOpts = (filterOptions.personal_account_owners || []).map((r) => ({
      value: r.value,
      label: r.label,
      count: r.count,
    }));

    const registeredOnOpts = (filterOptions.registered_by || []).map((r) => ({
      value: r.value,
      label: r.label,
      count: r.count,
    }));

    const commentOpts = Array.isArray(filterOptions.comments)
      ? filterOptions.comments
        .map((c) => {
          const v = c?.value != null ? String(c.value).trim() : '';
          const lbl = c?.label != null ? String(c.label).trim() : v;
          const value = v || lbl;
          const label = lbl || v;
          return { value, label, count: c?.count };
        })
        .filter((o) => o.value !== '' || o.label !== '')
      : [];

    const imeiOpts = (filterOptions.imei || []).map((item) => ({
      value: item.value,
      label: item.label,
      count: item.count,
    }));

    const macOpts = (filterOptions.mac_address || []).map((item) => ({
      value: item.value,
      label: item.label,
      count: item.count,
    }));

    return {
      modem: modemStatusOpts,
      sim: simStatusOpts,
      geo: geoOpts,
      server: serverOpts,
      modemNumber: modemNumberOpts,
      phoneNumber: phoneNumberOpts,
      operator: operatorOpts,
      amount: amountOpts,
      registeredBy: registeredByOpts,
      registeredOn: registeredOnOpts,
      comment: commentOpts,
      imei: imeiOpts,
      imeiEmptyCount: Number(filterOptions.imei_empty_count) || 0,
      mac: macOpts,
      macEmptyCount: Number(filterOptions.mac_empty_count) || 0,
    };
  }, [
    filterOptions,
    stats,
    modemNumberFilterOptions,
    modemNumberTableFilterOptions,
    phoneNumberFilterOptions,
  ]);

  const modemStatusCountsForFilterPanel = useMemo(() => {
    const scoped = scopedPanelStats?.modemCounts;
    const byValue = (columnFilterOptions.modem || []).reduce((acc, item) => {
      acc[item.value] = item.count || 0;
      return acc;
    }, {});

    return ['available', 'unavailable', 'unknown', 'disabled'].map((value) => ({
      value,
      label: MODEM_STATUS_LABELS[value] || value,
      count: scoped ? (scoped[value] || 0) : (byValue[value] || 0),
      countColor: value === 'available'
        ? 'green'
        : value === 'unavailable' || value === 'disabled'
          ? 'red'
          : 'white',
    }));
  }, [columnFilterOptions.modem, scopedPanelStats]);

  const simStatusCountsForFilterPanel = useMemo(() => {
    const scoped = scopedPanelStats?.simCounts;
    const row72FromApi = (filterOptions.sim_special_filters || []).find(
      (s) => s.value === SIM_FILTER_MODEM_72H_TOKEN
    );
    const base = (filterOptions.sim_statuses || []).map((s) => {
      const colorMap = {
        working: 'green',
        replace: 'red',
        check: 'yellow',
        free: 'blue',
        frozen: 'cyan',
      };
      return {
        value: s.value,
        label: SIM_STATUS_LABELS[s.value] || s.label,
        count: scoped ? (scoped[s.value] || 0) : s.count,
        countColor: colorMap[s.value] || 'white',
      };
    });
    const count72 = scoped
      ? (scoped[SIM_FILTER_MODEM_72H_TOKEN] ?? (row72FromApi != null ? Number(row72FromApi.count) || 0 : 0))
      : (row72FromApi != null ? Number(row72FromApi.count) || 0 : 0);
    const special = [
      {
        value: SIM_FILTER_MODEM_72H_TOKEN,
        label: row72FromApi?.label || SIM_FILTER_MODEM_72H_LABEL,
        count: count72,
        countColor: 'red',
      },
    ];
    return [...base, ...special];
  }, [filterOptions.sim_statuses, filterOptions.sim_special_filters, scopedPanelStats]);

  const geoCountsForFilterPanel = useMemo(() => {
    const scoped = scopedPanelStats?.geoCounts;
    return (filterOptions.geos || []).map((g) => ({
      value: g.value,
      label: g.label,
      count: scoped ? (scoped[g.value] || 0) : g.count,
      countColor: 'gray',
    }));
  }, [filterOptions.geos, scopedPanelStats]);

  const paymentCountsForFilterPanel = useMemo(() => {
    const scoped = scopedPanelStats?.paymentCounts;
    return [
      {
        value: 'today',
        label: 'Сегодня',
        count: scoped ? (scoped.today || 0) : (stats.upcoming_payments?.today ?? 0),
        countColor: 'red',
      },
      {
        value: 'tomorrow',
        label: 'Завтра',
        count: scoped ? (scoped.tomorrow || 0) : (stats.upcoming_payments?.tomorrow ?? 0),
        countColor: 'blue',
      },
      {
        value: '3days',
        label: 'Через 3 дня',
        count: scoped ? (scoped['3days'] || 0) : (stats.upcoming_payments?.['3days'] ?? 0),
        countColor: 'white',
      },
    ];
  }, [scopedPanelStats, stats.upcoming_payments]);

  // Текущая строка для инфо-панели (автоматически обновляется при изменении simData)
  const infoRow = simData.find((r) => r.id === infoRowId) || null;

  return (
    <div
      className="page-content sim-manager-page pm-content-fade-in"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 0 : 12,
        padding: isMobile ? 0 : 16,
        paddingBottom: isMobile ? 56 : 16,
        minHeight: '100%',
        boxSizing: 'border-box',
        background: isMobile ? '#ffffff' : '#f5f5f5',
      }}
    >
      {/* Сообщение об ошибке */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            background: '#FEE2E2',
            border: '1px solid #FECACA',
            borderRadius: 8,
            color: '#DC2626',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              color: '#DC2626',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Сообщение об успехе */}
      {successMessage && (
        <div
          style={{
            padding: '12px 16px',
            background: '#D1FAE5',
            border: '1px solid #A7F3D0',
            borderRadius: 8,
            color: '#065F46',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              color: '#065F46',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Хедер страницы — по макету node 383-69908 */}
      <SimManagerHeader
        title="Sim-менеджер"
        statusCounts={statusCounts}
        activeStatus={activeStatus}
        onStatusChange={handleStatusChange}
        simReserves={simReserves}
        activeReserve={
          filters.sim_status?.startsWith('reserve_')
            ? filters.sim_status.replace('reserve_', '')
            : null
        }
        onSimReserveClick={handleSimReserveClick}
        totalPayments={totalPayments}
        avgTariffPrice={avgTariffPrice}
        selectedCount={selectedRows.length}
        onRefresh={handleRefresh}
        onFilter={handleFilter}
        isFilterActive={isFilterPanelVisible}
        onEdit={handleEdit}
        onExport={handleExport}
        onExportEsia={handleExportEsia}
        onAdd={handleAdd}
        onDelete={handleBulkDelete}
        onProxyCheck={handleBulkProxyCheck}
        isProxyChecking={proxyCheckLoading}
        onImeiCheck={handleImeiMismatchCheck}
        isImeiCheckActive={Boolean(filters.imei_mismatch)}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearAllFilters}
        isMobile={isMobile}
      />

      {/* Контейнер для таблицы и панели фильтров */}
      <div
        className="sim-manager-page__body"
        style={{
          display: 'flex',
          gap: isMobile ? 0 : 12,
          alignItems: 'stretch',
          flex: 1,
          minHeight: 0,
          overflow: 'visible',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        {/* Таблица SIM-карт — по макету node 383-72675 */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: 'flex 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
          }}
        >
          {/* Loading overlay */}
          {isLoading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
            >
              <div style={{ fontSize: 16, color: '#666' }}>Загрузка...</div>
            </div>
          )}

          <SimTable
            data={filteredSimData}
            activeRowId={activeRowId}
            selectedRows={selectedRows}
            onSort={handleSort}
            onFilter={handleTableFilter}
            onRowSelect={handleRowSelect}
            onBulkSelect={handleBulkSelect}
            onAllSelect={handleAllSelect}
            onActionClick={handleActionClick}
            onRowClick={(row) => handleActionClick(row, 'info')}
            onCellEdit={handleCellEdit}
            onMarkPaid={handleMarkPaid}
            editOptions={tableEditOptions}
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            columnFilterOptions={columnFilterOptions}
            resetFiltersToken={tableFiltersResetToken}
            operatorTemplates={operatorTemplatesForLk}
          />
        </div>

        {/* Панель фильтров — по макету node 436:93348, mobile 574:96774 */}
        <SimFilterPanel
          isVisible={isFilterPanelVisible}
          onApply={handleFilterApply}
          onReset={handleFilterReset}
          onClose={() => setIsFilterPanelVisible(false)}
          onServerFilterDraftChange={handleFilterPanelServerDraftChange}
          isMobile={isMobile}
          modemStatusCounts={modemStatusCountsForFilterPanel}
          simStatusCounts={simStatusCountsForFilterPanel}
          geoCounts={geoCountsForFilterPanel}
          paymentCounts={paymentCountsForFilterPanel}
          serverOptions={filterOptions.servers.map((s) => ({
            value: s.value,
            label: s.label,
          }))}
          modemNumberOptions={modemNumberFilterOptions}
          modemNumberOptionsByServer={modemNumberFilterOptionsByServer}
          phoneNumberOptions={phoneNumberFilterOptions}
          registeredByOptions={filterOptions.registered_by.map((r) => ({
            value: r.value,
            label: r.label,
          }))}
          personalAccountOwnerOptions={(filterOptions.personal_account_owners || []).map((p) => ({
            value: p.value,
            label: p.label,
          }))}
          operatorOptions={(filterOptions.operators || []).map((o) => ({
            value: o.value,
            label: o.label,
          }))}
        />

        {/* Инфо-панель SIM — по макету node 541-81608, mobile 574:101171 */}
        {!isMobile && (
          <SimInfoPanel
            isVisible={isInfoPanelVisible}
            row={infoRow}
            onClose={() => {
              setIsInfoPanelVisible(false);
              setInfoRowId(null);
              setActiveRowId(null);
            }}
            onDelete={(r) => {
              handleActionClick(r, 'delete');
              setIsInfoPanelVisible(false);
              setInfoRowId(null);
            }}
            onEdit={(r) => {
              setSelectedRows([r.id]);
              setIsEditPanelOpen(true);
              setIsInfoPanelVisible(false);
              setInfoRowId(null);
            }}
          />
        )}
      </div>

      {/* Панель редактирования SIM-карт — по макету node 436:73226 */}
      <SimEditPanel
        isOpen={isEditPanelOpen}
        selectedItems={simData.filter((item) => selectedRows.includes(item.id))}
        onClose={handleEditPanelClose}
        onSave={handleEditPanelSave}
      />

      {/* Панель добавления SIM-карт — по макету node 436:73829 */}
      <SimAddPanel
        isOpen={isAddPanelOpen}
        onClose={handleAddPanelClose}
        onAdd={handleAddPanelSave}
        geoOptions={filterOptions.geos.map((g) => ({ value: g.value, label: g.label }))}
        serverOptions={filterOptions.servers.map((s) => ({ value: s.value, label: s.label }))}
        registeredByOptions={filterOptions.registered_by.map((r) => ({ value: r.value, label: r.label }))}
      />

      {/* Мобильная инфо-панель SIM — overlay bottom-sheet (574:101171) */}
      {isMobile && (
        <SimInfoPanel
          isVisible={isInfoPanelVisible}
          isMobile
          row={infoRow}
          onClose={() => {
            setIsInfoPanelVisible(false);
            setInfoRowId(null);
            setActiveRowId(null);
          }}
          onDelete={(r) => {
            handleActionClick(r, 'delete');
            setIsInfoPanelVisible(false);
            setInfoRowId(null);
          }}
          onEdit={(r) => {
            setSelectedRows([r.id]);
            setIsEditPanelOpen(true);
            setIsInfoPanelVisible(false);
            setInfoRowId(null);
          }}
        />
      )}

      {/* Модалка подтверждения оплаты */}
      {paymentConfirmRow && (
        <div
          className="sim-payment-confirm-overlay"
          {...paymentConfirmBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label="Подтверждение оплаты"
        >
          <div
            className="sim-payment-confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sim-payment-confirm__title">Подтверждение оплаты</div>
            <div className="sim-payment-confirm__body">
              <p>
                Отметить оплату для{' '}
                <strong>{paymentConfirmRow.phoneNumber || `SIM #${paymentConfirmRow.id}`}</strong>?
              </p>
              {paymentConfirmRow.amount > 0 && (
                <p className="sim-payment-confirm__amount">
                  Сумма: <strong>{paymentConfirmRow.amount} ₽</strong>
                </p>
              )}
              <p className="sim-payment-confirm__hint">
                Последний платёж → сегодня, следующий → через 30 дней
              </p>
            </div>
            <div className="sim-payment-confirm__actions">
              <button
                type="button"
                className="sim-payment-confirm__btn sim-payment-confirm__btn--cancel"
                onClick={() => setPaymentConfirmRow(null)}
              >
                Отмена
              </button>
              <button
                type="button"
                className="sim-payment-confirm__btn sim-payment-confirm__btn--confirm"
                onClick={handleConfirmPayment}
                autoFocus
              >
                Подтвердить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimManager;
